import React, { useEffect, useMemo, useState } from "react";
import InputMask from "react-input-mask";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Save, X } from "lucide-react";
import {
  listarUsuariosZenTagDoCliente,
  criarUsuarioZenTagDoCliente,
  atualizarUsuarioZenTagDoCliente,
} from "../services/api";

const inputClass = `
  w-full px-3 py-2.5 rounded-xl border outline-none transition
  zen-card text-gray-900 placeholder:text-gray-400
  focus:ring-2 focus:ring-blue-500/60
  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
  dark:focus:ring-blue-400/40
`;

const labelClass = "block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1";

const UserModal = ({ show, onClose, mode, initial, onSave }) => {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    senha: "",
  });

  useEffect(() => {
    if (!show) return;
    setForm({
      nome: initial?.nome || "",
      email: initial?.email || "",
      telefone: initial?.telefone || "",
      senha: "",
    });
  }, [show, initial]);

  const submit = async (e) => {
    e.preventDefault();

    if (!isEdit) {
      if (!form.nome?.trim()) return alert("Nome é obrigatório.");
      if (!form.email?.trim()) return alert("Email é obrigatório.");
      if (!form.senha?.trim()) return alert("Senha é obrigatória no cadastro.");
    } else {
      // no edit: senha pode ficar vazia
      if (!form.email?.trim()) return alert("Email é obrigatório.");
    }

    await onSave(form);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full max-w-xl rounded-2xl zen-card p-6 shadow-xl dark:backdrop-blur dark:bg-slate-950/70 dark:border-white/10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {isEdit ? "Editar Usuário (ZenTag)" : "Novo Usuário (ZenTag)"}
            </h3>
            <p className="text-sm text-gray-500 dark:text-slate-300/80">
              {isEdit ? "Altere email e/ou senha." : "Crie um novo usuário para o estabelecimento."}
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10"
            title="Fechar"
          >
            <X size={18} className="text-gray-700 dark:text-white" />
          </button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          {/* nome */}
          <div>
            <label className={labelClass}>Nome</label>
            <input
              value={form.nome}
              onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
              className={inputClass}
              placeholder="Nome do usuário"
              required={!isEdit}
              disabled={isEdit} // ✅ se você quer só email/senha no edit, deixa travado
            />
            {isEdit && (
              <p className="text-xs mt-1 text-gray-500 dark:text-slate-300/70">
                Nome vem do ZenTag (não editamos por aqui).
              </p>
            )}
          </div>

          {/* telefone */}
          <div>
            <label className={labelClass}>Telefone</label>
            <InputMask
              mask="(99) 99999-9999"
              value={form.telefone}
              onChange={(e) => setForm((p) => ({ ...p, telefone: e.target.value }))}
              className={inputClass}
              placeholder="(11) 99999-9999"
              disabled={isEdit} // ✅ mantém travado no edit
            />
          </div>

          {/* email */}
          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              className={inputClass}
              required
            />
          </div>

          {/* senha */}
          <div>
            <label className={labelClass}>
              {isEdit ? "Nova senha (opcional)" : "Senha"}
            </label>
            <input
              type="password"
              value={form.senha}
              onChange={(e) => setForm((p) => ({ ...p, senha: e.target.value }))}
              className={inputClass}
              placeholder={isEdit ? "Deixe em branco para não alterar" : ""}
              required={!isEdit}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="
                px-4 py-2 rounded-xl font-semibold text-sm
                bg-gray-100 hover:bg-gray-200 text-gray-800
                dark:bg-white/10 dark:hover:bg-white/15 dark:text-white
              "
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm bg-blue-600 text-white hover:bg-blue-500"
            >
              <Save size={16} />
              {isEdit ? "Salvar" : "Criar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ZentagUsuarios = ({ token }) => {
  const { id } = useParams(); // licId
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("new"); // "new" | "edit"
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listarUsuariosZenTagDoCliente(id, token);
      setUsuarios(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error(e);
      setUsuarios([]);
      alert("Erro ao listar usuários do ZenTag.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const rows = useMemo(() => {
    // se o ZenTag não tiver id no JSON, usamos index — mas normalmente tem.
    return usuarios;
  }, [usuarios]);

  const openNew = () => {
    setSelected(null);
    setModalMode("new");
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setSelected(u);
    setModalMode("edit");
    setModalOpen(true);
  };

  const onSave = async (form) => {
    try {
      if (modalMode === "new") {
        await criarUsuarioZenTagDoCliente(
          id,
          {
            Nome: form.nome,
            Email: form.email,
            Telefone: form.telefone,
            Senha: form.senha,
          },
          token
        );
        alert("Usuário criado com sucesso!");
      } else {
        await atualizarUsuarioZenTagDoCliente(
          id,
          selected.id,
          {
            Email: form.email,
            Senha: form.senha, // pode ir vazio -> backend ignora
          },
          token
        );
        alert("Usuário atualizado com sucesso!");
      }

      setModalOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      const data = e?.response?.data;
      const msg =
        typeof data === "string"
          ? data
          : data?.message ||
            (data?.errors && Object.values(data.errors).flat().join("\n")) ||
            "Erro ao salvar usuário.";
      alert(msg);
    }
  };

  return (
    <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
      <div className="flex items-center justify-between gap-4">
        <button
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-slate-200 dark:hover:text-white"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} /> Voltar
        </button>

        <button
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm bg-blue-600 text-white hover:bg-blue-500"
          onClick={openNew}
        >
          <Plus size={18} /> Novo usuário
        </button>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-4">
        Usuários do ZenTag <span className="text-gray-500 dark:text-slate-400"> (Licença #{id})</span>
      </h2>

      {loading ? (
        <div className="mt-4 text-gray-600 dark:text-slate-200">Carregando...</div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <div className="min-w-full rounded-2xl zen-card dark:bg-white/5 dark:border-white/10 overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-white/5">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Nome</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Email</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Telefone</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Ações</th>
                </tr>
              </thead>

              <tbody className="text-gray-700 dark:text-slate-200">
                {rows.map((u, idx) => (
                  <tr
                    key={u.id ?? idx}
                    className="border-t border-gray-200 hover:bg-gray-50 transition dark:border-white/10 dark:hover:bg-white/5"
                  >
                    <td className="py-3 px-4">{u.nome || "-"}</td>
                    <td className="py-3 px-4">{u.email || "-"}</td>
                    <td className="py-3 px-4">{u.telefone || "-"}</td>
                    <td className="py-3 px-4 text-center">
                      <button
                        className="p-2 rounded-full transition text-blue-600 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-500/10"
                        onClick={() => openEdit(u)}
                        title="Editar (Email/Senha)"
                      >
                        <Edit size={18} />
                      </button>
                    </td>
                  </tr>
                ))}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-10 text-center text-gray-500 dark:text-slate-300/80">
                      Nenhum usuário encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <UserModal
        show={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        initial={selected}
        onSave={onSave}
      />
    </div>
  );
};

export default ZentagUsuarios;
